import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../../services/axios';
import toast from 'react-hot-toast';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { onboardingApi } from '../../features/onboarding/api';
import { A, AdminCard, AdminPageShell, AdminBadge } from './adminStyles';

export default function AdminInterns() {
    const [users, setUsers]           = useState([]);
    const [lifecycle, setLifecycle]   = useState({});
    const [loading, setLoading]       = useState(true);
    const [search, setSearch]         = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [linkBusy, setLinkBusy]     = useState(null);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const [usersRes, lifeRes] = await Promise.allSettled([
                    axios.get('/users', { params: { page: 1, limit: 100 } }),
                    axios.get('/lifecycle/admin/summary'),
                ]);
                if (usersRes.status === 'fulfilled') {
                    const payload = usersRes.value.data.data;
                    setUsers(payload?.users || []);
                }
                if (lifeRes.status === 'fulfilled') {
                    const map = {};
                    (lifeRes.value.data.data || []).forEach((row) => { map[row.userId] = row; });
                    setLifecycle(map);
                }
            } catch (err) {
                toast.error('Unable to load interns');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const interns = useMemo(() =>
        users.filter((u) => {
            const roleNames = (u.Roles || u.roles || []).map((r) => typeof r === 'string' ? r : r.name);
            return roleNames.includes('intern') || lifecycle[u.id];
        }),
    [users, lifecycle]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return interns.filter((u) => {
            const life   = lifecycle[u.id];
            const status = life?.status || 'unknown';
            if (statusFilter !== 'all' && status !== statusFilter) return false;
            if (!q) return true;
            return `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''}`.toLowerCase().includes(q);
        });
    }, [interns, search, statusFilter, lifecycle]);

    const statuses = useMemo(() => {
        const set = new Set(Object.values(lifecycle).map((r) => r.status).filter(Boolean));
        return ['all', ...Array.from(set).sort()];
    }, [lifecycle]);

    const copyOnboardingLink = async (userId, name) => {
        try {
            setLinkBusy(userId);
            const res  = await onboardingApi.adminLink(userId);
            const link = res.link;
            if (!link) { toast.error('No link returned'); return; }
            await navigator.clipboard.writeText(link);
            toast.success(`Onboarding link copied for ${name}`);
        } catch (err) {
            toast.error(err.message || 'Unable to create onboarding link');
        } finally {
            setLinkBusy(null);
        }
    };

    const statusColor = (s) => {
        if (!s || s === 'unknown')                        return { bg: A.gray,   color: A.inkSoft };
        if (['completed', 'talent_pool'].includes(s))     return { bg: A.sageL,  color: A.sage   };
        if (['active', 'in_progress'].includes(s))        return { bg: A.tealL,  color: A.teal   };
        if (['not_started'].includes(s))                  return { bg: A.gray,   color: A.inkSoft };
        return { bg: A.goldL, color: A.gold };
    };

    return (
        <AdminPageShell
            eyebrow="People"
            title="Intern Management"
            description="Search and manage all interns across the program."
            actions={
                <Link to="/admin/onboarding" style={{ display: 'inline-flex', alignItems: 'center', padding: '7px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#fff', textDecoration: 'none', background: A.teal }}>
                    + Add Intern
                </Link>
            }
        >
            {/* Search / filter bar */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <input
                    style={{ flex: 1, minWidth: 220, padding: '8px 12px', border: `1px solid ${A.border}`, borderRadius: 8, fontSize: 13, color: A.ink, outline: 'none', background: '#fff' }}
                    placeholder="Search name or email…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <select
                    style={{ padding: '8px 12px', border: `1px solid ${A.border}`, borderRadius: 8, fontSize: 13, color: A.ink, background: '#fff', minWidth: 160 }}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    {statuses.map((s) => (
                        <option key={s} value={s}>{s === 'all' ? 'All statuses' : s}</option>
                    ))}
                </select>
            </div>

            {loading ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[1, 2, 3].map(i => <div key={i} style={{ height: 48, background: '#f0f3f2', borderRadius: 10, animation: 'pulse 1.5s infinite' }} />)}
                </div>
            ) : filtered.length === 0 ? (
                <AdminCard style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <p style={{ color: A.inkSoft, fontSize: 13 }}>No interns match your filters.</p>
                </AdminCard>
            ) : (
                <AdminCard noPad>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: A.gray, borderBottom: `1px solid ${A.border}` }}>
                                    {['Name', 'Email', 'Status', 'Profile %', 'Actions'].map(h => (
                                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: A.inkSoft, textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((u) => {
                                    const life = lifecycle[u.id];
                                    const sc   = statusColor(life?.status);
                                    return (
                                        <tr key={u.id}
                                            style={{ borderBottom: `1px solid ${A.border}` }}
                                            onMouseEnter={e => e.currentTarget.style.background = A.tealL}
                                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                        >
                                            <td style={{ padding: '10px 14px', fontWeight: 600, color: A.ink }}>{u.first_name} {u.last_name}</td>
                                            <td style={{ padding: '10px 14px', color: A.inkMid }}>{u.email}</td>
                                            <td style={{ padding: '10px 14px' }}>
                                                <AdminBadge label={life?.status || '—'} bg={sc.bg} color={sc.color} />
                                            </td>
                                            <td style={{ padding: '10px 14px', color: A.inkMid }}>{life?.progress ?? '—'}</td>
                                            <td style={{ padding: '10px 14px' }}>
                                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                    <button
                                                        type="button"
                                                        disabled={linkBusy === u.id}
                                                        onClick={() => copyOnboardingLink(u.id, `${u.first_name} ${u.last_name}`)}
                                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', border: `1px solid ${A.border}`, borderRadius: 7, fontSize: 11.5, fontWeight: 600, color: A.teal, background: '#fff', cursor: 'pointer' }}
                                                    >
                                                        <ClipboardDocumentIcon style={{ width: 14, height: 14 }} />
                                                        Onboarding link
                                                    </button>
                                                    <Link to="/admin/onboarding"
                                                        style={{ display: 'inline-flex', alignItems: 'center', padding: '5px 10px', borderRadius: 7, fontSize: 11.5, fontWeight: 600, color: '#fff', background: A.teal, textDecoration: 'none' }}>
                                                        Track
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </AdminCard>
            )}
        </AdminPageShell>
    );
}
